import { useMemo } from 'react';
import {
  CustomMultiSelectFilter,
  FiltersBar,
  SelectFilter,
  SwitchFilter,
  TextFilter,
} from '@britishcouncil/react-admin';
import { usePermissions } from '@britishcouncil/react-common';

import { Permission } from 'api';
import { ExaminerListFilterType } from 'store';
import {
  useAllCountriesOptions,
  useLocationFiltersCascade,
  getLocationFilterDefinitions,
  LocationFilterDefinitions,
} from 'hooks';
import styled from '@emotion/styled';
import { CERTIFICATION_STATUS_OPTIONS } from 'const/certification-status/certificationStatusOptions';

type ExaminerListBaseFilters = {
  searchText: TextFilter;
  speakingStatuses: CustomMultiSelectFilter<number>;
  writingStatuses: CustomMultiSelectFilter<number>;
  onlineSpeakingStatuses: CustomMultiSelectFilter<number>;
  lifeSkillsStatuses: CustomMultiSelectFilter<number>;
  countryOfResidenceId: SelectFilter<number>;
} & LocationFilterDefinitions;

type ExaminerListFiltersWithNomads = ExaminerListBaseFilters & {
  showNomads: SwitchFilter;
};

type ExaminerListFiltersType = ExaminerListBaseFilters | ExaminerListFiltersWithNomads;

export type ExaminerListFiltersBarProps = {
  initialFilterState: ExaminerListFilterType;
  defaultFilterState?: ExaminerListFilterType;
  setFilterState: (filterState: ExaminerListFilterType) => void;
};

export const ExaminerListFiltersBar = ({
  initialFilterState,
  defaultFilterState,
  setFilterState,
}: ExaminerListFiltersBarProps) => {
  const { permissions } = usePermissions();
  const allCountriesOptions = useAllCountriesOptions();

  const {
    filtersValues,
    regionsOptions,
    countriesOptions,
    centresOptions,
    onFiltersApply,
    onFiltersChange,
  } = useLocationFiltersCascade<ExaminerListFiltersType, ExaminerListFilterType>(
    initialFilterState as never,
    setFilterState
  );

  const canViewNomads = permissions.includes(Permission.ViewNomadsExaminers);

  const filtersDefinition = useMemo(
    (): ExaminerListFiltersType => ({
      searchText: {
        type: 'text',
        label: 'Search',
        isMain: true,
        placeholder: 'Search by name or examiner number...',
        className: 'filter-component',
      },
      ...getLocationFilterDefinitions({
        regionsOptions,
        countriesOptions,
        centresOptions,
        centreType: filtersValues.centreType,
      }),
      countryOfResidenceId: {
        type: 'select',
        label: 'Country / Territory Of Residence',
        className: 'filter-component',
        options: allCountriesOptions.map((o) => ({ text: o.text, value: o.value })),
        dropdownOptions: {
          placeholder: 'Select...',
          clearable: true,
          search: true,
        },
      },
      speakingStatuses: {
        type: 'custom-multiselect',
        label: 'Speaking Status',
        options: CERTIFICATION_STATUS_OPTIONS,
        dropdownOptions: {
          placeholder: 'Select...',
          showAllSelected: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
      },
      onlineSpeakingStatuses: {
        type: 'custom-multiselect',
        label: 'Online Speaking Status',
        options: CERTIFICATION_STATUS_OPTIONS,
        dropdownOptions: {
          placeholder: 'Select...',
          showAllSelected: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
      },
      writingStatuses: {
        type: 'custom-multiselect',
        label: 'Writing Status',
        options: CERTIFICATION_STATUS_OPTIONS,
        dropdownOptions: {
          placeholder: 'Select...',
          showAllSelected: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
      },
      lifeSkillsStatuses: {
        type: 'custom-multiselect',
        label: 'Life Skills Status',
        options: CERTIFICATION_STATUS_OPTIONS,
        dropdownOptions: {
          placeholder: 'Select...',
          showAllSelected: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
      },
      ...(canViewNomads
        ? {
            showNomads: {
              type: 'switch' as const,
              label: 'Include Nomads',
              isMain: true,
            },
          }
        : {}),
    }),
    [
      regionsOptions,
      countriesOptions,
      centresOptions,
      filtersValues.centreType,
      allCountriesOptions,
      canViewNomads,
    ]
  );

  return (
    <S.FiltersBar
      value={filtersValues}
      filters={filtersDefinition}
      clearable
      expanded
      onFiltersApply={onFiltersApply}
      onFiltersChange={onFiltersChange}
      defaultValues={defaultFilterState ?? initialFilterState}
    />
  );
};

const S = {
  FiltersBar: styled(FiltersBar)`
    #searchText-label + div {
      min-width: 300px;
    }
  `,
};
